import { useState, useCallback, useEffect, useMemo, useRef } from 'react';

import { Switch, FormControlLabel, Box, Typography } from '@mui/material';

import strings from 'constants/index';
import { PaginationParams } from 'enums/paginationParams';

import { usePaginationQueryParams } from './usePaginationQueryParams';

export const useTestDataSwitch = (testDataFilter?: PaginationFilter) => {
  const [hiddenTestData, setHiddenTestData] = useState(true);
  const isInitialMount = useRef(true);

  const {
    handlePaginationQueryParamsChange,
    paginationQueryParams,
    filterQueryParamsParse,
    filterQueryParamsStringify,
  } = usePaginationQueryParams();

  const filterParam = paginationQueryParams.get(PaginationParams.Filter);

  // Memoize the filter string to avoid recalculating on every render
  const testDataFilterString = useMemo(
    () => (testDataFilter ? filterQueryParamsStringify([testDataFilter]) : ''),
    [testDataFilter, filterQueryParamsStringify],
  );

  useEffect(() => {
    if (!testDataFilter || !testDataFilterString) return;

    const isQueryParamsIncludesTestFilter = filterParam?.includes(testDataFilterString) ?? false;

    // Only update on initial mount or when filterParam actually changes
    if (isInitialMount.current) {
      setHiddenTestData(isQueryParamsIncludesTestFilter);
      isInitialMount.current = false;
    } else {
      // Only update if there's a real change in the filter param
      const currentState = hiddenTestData;
      if (currentState !== isQueryParamsIncludesTestFilter) {
        setHiddenTestData(isQueryParamsIncludesTestFilter);
      }
    }
  }, [filterParam, testDataFilter, testDataFilterString]);

  const handleChangeHiddenData = useCallback(
    (hiddenTestData: boolean) => {
      if (testDataFilter) {
        let filtersResult: Array<PaginationFilter>;
        if (filterParam) {
          const filters = filterQueryParamsParse(filterParam);
          filters.filter(x => x.columnId === 'locality.id');
          filtersResult = hiddenTestData
            ? [...filters, testDataFilter]
            : filters.filter(filter => filter.columnId !== testDataFilter.columnId);
        } else {
          filtersResult = [testDataFilter];
        }
        handlePaginationQueryParamsChange({ filters: filtersResult });
      }
      setHiddenTestData(hiddenTestData);
    },
    [filterParam, filterQueryParamsParse, handlePaginationQueryParamsChange, testDataFilter],
  );

  const TestDataSwitch = useMemo(
    () => () => (
      <FormControlLabel
        control={
          <Switch
            data-testid="test-data-switch"
            aria-label="test-data-view"
            checked={!hiddenTestData}
            onChange={() => handleChangeHiddenData(!hiddenTestData)}
            name="testDataView"
          />
        }
        label={
          <Box>
            <Typography variant="body1">{strings.show_test_data}</Typography>
            {!hiddenTestData && (
              <Typography variant="subtitle1">{strings.test_data_visible}</Typography>
            )}
          </Box>
        }
      />
    ),
    [hiddenTestData, handleChangeHiddenData],
  );

  return { hiddenTestData, TestDataSwitch };
};
